import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

const STATUS_BG_CLASSES = {
  saved: "bg-status-saved",
  contacted: "bg-status-contacted",
  viewing_booked: "bg-status-viewing-booked",
  viewed: "bg-status-viewed",
  applied: "bg-status-applied",
  accepted: "bg-status-accepted",
  rejected: "bg-status-rejected"
}

const DRAG_DELAY = 300

export default class extends Controller {
  static targets = ["column", "count"]
  static values = { group: String }

  connect() {
    this.dragging = false
    this.preventContextMenu = (e) => e.preventDefault()
    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)

    this.sortables = this.columnTargets.map(column => {
      column.addEventListener("contextmenu", this.preventContextMenu)
      column.addEventListener("touchstart", this.onTouchStart, { passive: true })

      return Sortable.create(column, {
        group: this.groupValue || "kanban",
        draggable: "[data-property-id]",
        forceFallback: true,
        animation: 150,
        delay: DRAG_DELAY,
        delayOnTouchOnly: true,
        touchStartThreshold: 5,
        ghostClass: "opacity-30",
        scroll: true,
        bubbleScroll: true,
        scrollSensitivity: 80,
        scrollSpeed: 15,
        onStart: this.onDragStart.bind(this),
        onMove: this.onDragMove.bind(this),
        onEnd: this.onDragEnd.bind(this)
      })
    })
  }

  disconnect() {
    this.columnTargets.forEach(col => {
      col.removeEventListener("contextmenu", this.preventContextMenu)
      col.removeEventListener("touchstart", this.onTouchStart)
    })
    document.removeEventListener("touchmove", this.onTouchMove)
    document.removeEventListener("touchend", this.onTouchEnd)
    if (this.momentumFrame) cancelAnimationFrame(this.momentumFrame)
    this.sortables?.forEach(s => s.destroy())
  }

  // --- Touch-based manual scroll (while touch-action: none blocks browser scroll) ---

  onTouchStart(e) {
    // Stop any in-flight momentum from a previous swipe
    if (this.momentumFrame) {
      cancelAnimationFrame(this.momentumFrame)
      this.momentumFrame = null
    }

    this.dragging = false
    this.touchX = e.touches[0].clientX
    this.touchY = e.touches[0].clientY
    this.lastMoveTime = Date.now()
    this.velocityX = 0
    this.velocityY = 0
    this.activeScrollContainer = e.target.closest(".overflow-x-auto")

    document.addEventListener("touchmove", this.onTouchMove, { passive: false })
    document.addEventListener("touchend", this.onTouchEnd)
  }

  onTouchMove(e) {
    if (this.dragging) {
      e.preventDefault()
      return
    }

    const x = e.touches[0].clientX
    const y = e.touches[0].clientY
    const now = Date.now()
    const dt = Math.max(now - this.lastMoveTime, 1)
    const dx = this.touchX - x
    const dy = this.touchY - y

    // Exponential moving average for smooth velocity
    this.velocityX = 0.6 * (dx / dt * 16) + 0.4 * this.velocityX
    this.velocityY = 0.6 * (dy / dt * 16) + 0.4 * this.velocityY

    if (this.activeScrollContainer) {
      this.activeScrollContainer.scrollLeft += dx
    }
    window.scrollBy(0, dy)

    this.touchX = x
    this.touchY = y
    this.lastMoveTime = now
  }

  onTouchEnd() {
    document.removeEventListener("touchmove", this.onTouchMove)
    document.removeEventListener("touchend", this.onTouchEnd)

    if (!this.dragging && (Math.abs(this.velocityX) > 0.5 || Math.abs(this.velocityY) > 0.5)) {
      this.startMomentum()
    }
  }

  startMomentum() {
    const decel = 0.95
    const container = this.activeScrollContainer

    const tick = () => {
      this.velocityX *= decel
      this.velocityY *= decel

      if (Math.abs(this.velocityX) < 0.3 && Math.abs(this.velocityY) < 0.3) {
        this.momentumFrame = null
        return
      }

      if (container) container.scrollLeft += this.velocityX
      window.scrollBy(0, this.velocityY)
      this.momentumFrame = requestAnimationFrame(tick)
    }

    this.momentumFrame = requestAnimationFrame(tick)
  }

  // --- SortableJS callbacks ---

  onDragStart(event) {
    this.dragging = true
    this.currentDragTarget = event.from.dataset.status
    this.fallbackEl = document.querySelector(".sortable-fallback")
    this.columnTargets.forEach(col => {
      col.classList.add("ring-2", "ring-base-300", "ring-dashed")
    })
  }

  onDragMove(event) {
    const toStatus = event.to.dataset.status
    if (toStatus === this.currentDragTarget) return
    this.currentDragTarget = toStatus

    const card = this.fallbackEl?.querySelector(".card")
    if (!card) return

    Object.values(STATUS_BG_CLASSES).forEach(cls => card.classList.remove(cls))
    const newClass = STATUS_BG_CLASSES[toStatus]
    if (newClass) card.classList.add(newClass)
  }

  onDragEnd(event) {
    this.dragging = false
    this.currentDragTarget = null
    this.fallbackEl = null
    this.columnTargets.forEach(col => {
      col.classList.remove("ring-2", "ring-base-300", "ring-dashed")
    })

    const fromStatus = event.from.dataset.status
    const toStatus = event.to.dataset.status

    if (fromStatus === toStatus) return

    const item = event.item
    const propertyId = item.dataset.propertyId
    const card = item.querySelector(".card") || item.firstElementChild

    this.removePlaceholder(event.to)
    this.addPlaceholderIfEmpty(event.from)
    this.updateCardColor(card, fromStatus, toStatus)
    this.updateCounts(fromStatus, toStatus)
    this.persistTransition({ propertyId, toStatus, item, event, fromStatus, card })
  }

  // --- Helpers ---

  removePlaceholder(column) {
    column.querySelector(".sortable-empty-placeholder")?.remove()
  }

  addPlaceholderIfEmpty(column) {
    if (column.querySelector("[data-property-id]")) return
    const placeholder = document.createElement("div")
    placeholder.className = "w-full min-h-[80px] border-2 border-dashed border-base-300 rounded-lg flex items-center justify-center sortable-empty-placeholder"
    placeholder.innerHTML = '<span class="text-xs text-base-content/30">Drop here</span>'
    column.appendChild(placeholder)
  }

  updateCardColor(card, fromStatus, toStatus) {
    if (!card) return
    const oldClass = STATUS_BG_CLASSES[fromStatus]
    const newClass = STATUS_BG_CLASSES[toStatus]
    if (oldClass) card.classList.remove(oldClass)
    if (newClass) card.classList.add(newClass)
  }

  updateCounts(fromStatus, toStatus) {
    this.countTargets.forEach(el => {
      const status = el.dataset.status
      const current = parseInt(el.textContent, 10) || 0
      if (status === fromStatus) el.textContent = Math.max(0, current - 1)
      if (status === toStatus) el.textContent = current + 1
    })
  }

  async persistTransition({ propertyId, toStatus, item, event, fromStatus, card }) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch(`/properties/${propertyId}/transition`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({ status: toStatus })
      })

      if (!response.ok) throw new Error(`Server returned ${response.status}`)
    } catch {
      this.rollback({ item, event, fromStatus, toStatus, card })
    }
  }

  rollback({ item, event, fromStatus, toStatus, card }) {
    const fromColumn = this.columnTargets.find(c => c.dataset.status === fromStatus)
    const toColumn = this.columnTargets.find(c => c.dataset.status === toStatus)
    if (!fromColumn) return

    this.removePlaceholder(fromColumn)

    if (event.oldIndex < fromColumn.children.length) {
      fromColumn.insertBefore(item, fromColumn.children[event.oldIndex])
    } else {
      fromColumn.appendChild(item)
    }

    if (toColumn) this.addPlaceholderIfEmpty(toColumn)

    this.updateCardColor(card, toStatus, fromStatus)
    this.updateCounts(toStatus, fromStatus)

    item.classList.add("animate-pulse")
    setTimeout(() => item.classList.remove("animate-pulse"), 1500)
  }
}
